import { useEffect, useState, useRef, Fragment } from 'react';
import './index.scss';
import {
    Box,
    Container,
    TextField,
    InputAdornment,
    Typography,
    Card,
    CardMedia,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Chip,
    Checkbox,
    FormControlLabel,
    Badge,
    Tabs,
    Tab,
    IconButton,
    Paper,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    CircularProgress,
    Divider,
    Fab,
    SxProps,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Grid,
} from '@mui/material';
import {
    Search as SearchIcon,
    Clear as ClearIcon,
    KeyboardArrowUp as KeyboardArrowUpIcon,
    Add as AddIcon,
    Remove as RemoveIcon,
    NavigateBefore as NavigateBeforeIcon,
    NavigateNext as NavigateNextIcon,
    Refresh as RefreshIcon,
    ErrorOutline as ErrorOutlineIcon,
    InfoOutlined as InfoOutlinedIcon,
    FitnessCenter as FitnessCenterIcon,
    Restaurant as RestaurantIcon,
    HelpOutline as HelpOutlineIcon,
    Sort as SortIcon,
    ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon,
    SortByAlpha as SortByAlphaIcon,
    FilterList as FilterListIcon,
    FilterAlt as FilterAltIcon,
    Medication as MedicationIcon,
} from '@mui/icons-material';
import searchIcon from '@/assets/images/sources/search.svg';
import { useLoaderData, useRouteLoaderData } from 'react-router-dom';
import { BrandFilterData, PickLoaderData, StudioLoaderData } from '@/router/loader';
import {
    deletePartnersBlockProduct,
    getPartnersProductListWithBlockId,
    postPartnersBlock,
    putPartnersBlockProduct,
} from '@/services/partners.service';
import { useDebounce } from 'use-debounce';
import linkAddIcon from '@/assets/images/sources/link-add.svg';
import linkRemoveIcon from '@/assets/images/sources/link-remove.svg';
import gpRequestIcon from '@/assets/images/sources/gp-request.svg';
import { ReactSVG } from 'react-svg';
import { getProductDetail } from '@/services/mall.service';
import imageNotFound from '@/assets/images/errors/image_not_found.png';
import { PartnersBlockType } from '@/store/modules/spaceUserV2Store';
import { toast } from 'react-toastify';
import { request09ByProductId } from '@/services/sourcing.service';
import { getEventBlockV2 } from '@/services/studio.service';
import { handleWheelHorizontalScroll } from '@/utils/dom.common';
import RequestSourcingDialog from '@/pages/sourcing/Introduction/RequestSourcingDialog';
import { dialogOpen } from '@/modules/MuiDialog';
import livingIcon from '@/assets/images/sources/studioIcons/living.svg';
import fashionIcon from '@/assets/images/sources/studioIcons/fashion.svg';
import accessoryIcon from '@/assets/images/sources/studioIcons/acc.svg';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { apiUrls } from '@/constant/configure';
import api from '@/helpers/api_helper';
import { FilterOptionImage, FilterResponse, PickResponse } from './index.types';
import { RequestQueryBuilder, SFields } from '@nestjsx/crud-request';

const PickPage = () => {
    const {
        livingCategories,
        fashionCategories,
        accessoryCategories,
        athleisureCategories,
        foodCategories,
        supplementCategories,
        partnersBlockProductIds: _partnersBlockProductIds,
        addedProducts: _addedProducts,
        partnersBlocks: _partnersBlocks,
    } = useLoaderData() as PickLoaderData;
    const { spaceUserData } = useRouteLoaderData('/studio/:userId') as StudioLoaderData;
    const [filteredCategories, setFilteredCategories] = useState<typeof livingCategories>(athleisureCategories);
    const [brandFilterData, setBrandFilterData] = useState<BrandFilterData[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const [openDetailDialog, setOpenDetailDialog] = useState(false);
    const [dialogProductData, setDialogProductData] = useState<{
        mallId: string;
        productId: string;
        matchingProductGroupId: string;
        saleCommission: string;
    }>({
        mallId: '',
        productId: '',
        matchingProductGroupId: '',
        saleCommission: '',
    });

    const [productIdForAddingProfileLink, setProductIdForAddingProfileLink] = useState('');
    const [openAddingProfileLinkDialog, setOpenAddingProfileLinkDialog] = useState(false);
    const [productList, setProductList] = useState<PickResponse['data']>([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [refreshLoading, setRefreshLoading] = useState(false); // 프로필링크 추가 다이얼로그에서 새로고침 로딩 상태 추가
    const [request09Loading, setRequest09Loading] = useState(''); // 공구요청 다이얼로그;
    const limit = 20;
    const observer = useRef<IntersectionObserver | null>(null);
    const lastProductElementRef = useRef<HTMLDivElement | null>(null);
    const [partnersBlocks, setPartnersBlocks] = useState<PartnersBlockType[]>(_partnersBlocks);
    const [addedProducts, setAddedProducts] = useState<Set<string>>(new Set(_addedProducts));
    const [partnersBlockProductIds, setPartnersBlockProductIds] = useState<Record<string, string[]>>({
        ..._partnersBlockProductIds,
    });
    const [categoryType, setCategoryType] = useState<
        'living' | 'fashion' | 'accessory' | 'athleisure' | 'food' | 'supplement'
    >('athleisure');

    const [debouncedSearchTerm] = useDebounce(searchTerm, 1000);
    const [sortOption, setSortOption] = useState<'newest' | 'priceHigh' | 'priceLow' | 'name'>('newest');
    const [expandedFilter, setExpandedFilter] = useState(false);

    const [filterData, setFilterData] = useState<FilterResponse>([]);

    // 필터 옵션 선택 상태 관리
    const [selectedFilters, setSelectedFilters] = useState<Record<string, FilterResponse[0]['filterOptions'][0]>>({});
    const [appliedFilters, setAppliedFilters] = useState<Record<string, FilterResponse[0]['filterOptions'][0]>>({});

    // PickPage 컴포넌트 내 state 추가
    const [selectedBrandName, setSelectedBrandName] = useState<string | null>(null);

    useEffect(() => {
        const categoryId = filteredCategories?.[selectedTabIndex]?.categoryId;
        fetchProducts({
            offset: 0,
            limit,
            categoryId,
            keyword: debouncedSearchTerm,
            sort: sortOption,
            filterOptionIds: [...Object.keys(appliedFilters)],
        });
    }, [debouncedSearchTerm, filteredCategories, sortOption, appliedFilters]);

    useEffect(() => {
        const categoryId = filteredCategories?.[selectedTabIndex]?.categoryId;
        fetchFilter({ categoryId });
    }, [filteredCategories, selectedTabIndex]);

    useEffect(() => {
        if (categoryType === 'athleisure') {
            fetchBrandFilter();
        }
    }, [categoryType]);

    // Intersection Observer 설정
    useEffect(() => {
        if (observer.current) observer.current.disconnect();
        if (loading || !hasMore) return;

        observer.current = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore) {
                    fetchProducts({
                        offset: productList.length,
                        limit,
                        categoryId: filteredCategories?.[selectedTabIndex]?.categoryId,
                        keyword: searchTerm,
                        sort: sortOption,
                        filterOptionIds: [...Object.keys(appliedFilters)],
                        brandName: selectedBrandName || undefined,
                    });
                }
            },
            { threshold: 0.5 },
        );

        if (lastProductElementRef.current) {
            observer.current.observe(lastProductElementRef.current);
        } else {
            // 데이터가 있지만 ref가 없는 경우 추가 데이터 로드 시도
            if (productList.length > 0 && hasMore && !loading) {
                setTimeout(() => {
                    fetchProducts({
                        offset: productList.length,
                        limit,
                        categoryId: filteredCategories?.[selectedTabIndex]?.categoryId,
                        keyword: searchTerm,
                        sort: sortOption,
                        filterOptionIds: [...Object.keys(appliedFilters)],
                        brandName: selectedBrandName || undefined,
                    });
                }, 500);
            }
        }

        return () => {
            if (observer.current) {
                observer.current.disconnect();
            }
        };
    }, [loading, hasMore]);

    // 상품 데이터 불러오기 함수
    const fetchProducts = async ({
        offset,
        limit,
        categoryId,
        keyword,
        sort = 'newest',
        filterOptionIds = [],
        brandName = '',
    }: {
        offset: number;
        limit: number;
        categoryId?: string;
        keyword?: string;
        sort: typeof sortOption;
        filterOptionIds?: string[];
        brandName?: string;
    }) => {
        // 이미 로딩 중이거나 더 이상 불러올 데이터가 없는 경우 중복 호출 방지
        if (offset !== 0 && (loading || !hasMore)) {
            console.log('이미 로딩 중이거나 더 이상 불러올 데이터가 없습니다.');
            return;
        }

        console.log(
            `상품 로딩 시작: offset=${offset}, limit=${limit}, 카테고리=${filteredCategories?.find((item) => item.categoryId === categoryId)?.categoryName || '없음'}, 키워드=${keyword || '없음'}`,
        );
        setLoading(true);

        try {
            const qb = RequestQueryBuilder.create();

            const keyWordQuery: SFields[] = keyword ? [{ groupTitle: { $contL: keyword } }] : [];
            const priceFilterOptionIds = filterOptionIds?.filter((id) => id.includes('price'));
            const priceFilterQuery: SFields[] =
                priceFilterOptionIds?.length > 0
                    ? [
                          {
                              $or: priceFilterOptionIds?.map((v) => {
                                  const [_, price] = v?.split('_');
                                  switch (price) {
                                      case '1':
                                          return {
                                              'representMallProduct.productCharge': {
                                                  $between: [10000, 19999],
                                              },
                                          };
                                      case '2':
                                          return {
                                              'representMallProduct.productCharge': {
                                                  $between: [20000, 29999],
                                              },
                                          };
                                      case '3':
                                          return {
                                              'representMallProduct.productCharge': {
                                                  $between: [30000, 39999],
                                              },
                                          };
                                      case '4':
                                          return {
                                              'representMallProduct.productCharge': {
                                                  $between: [40000, 49999],
                                              },
                                          };
                                      case '5':
                                          return {
                                              'representMallProduct.productCharge': {
                                                  $gte: 50000,
                                              },
                                          };
                                      default:
                                          return {
                                              'representMallProduct.productCharge': {
                                                  $gte: 0,
                                              },
                                          };
                                  }
                              }),
                          },
                      ]
                    : [];
            const brandFilterQuery: SFields[] = brandName ? [{ 'filterOptions.value': { $in: [brandName] } }] : [];
            filterOptionIds = filterOptionIds?.filter((id) => !id.includes('price'));
            const filterQuery: SFields[] =
                filterOptionIds?.length > 0
                    ? [
                          {
                              'filterOptions.id': {
                                  $in: filterOptionIds,
                              },
                          },
                      ]
                    : [];

            if (categoryId) {
                if (brandFilterQuery.length > 0) {
                    qb.search({
                        $and: [
                            ...keyWordQuery,
                            ...filterQuery,
                            ...priceFilterQuery,
                            {
                                $and: [
                                    ...brandFilterQuery,
                                    {
                                        $or: [
                                            { 'category.parentCategoryId': { $eq: categoryId } },
                                            { 'category.id': { $eq: categoryId } },
                                        ],
                                    },
                                ],
                            },
                        ],
                    });
                } else {
                    qb.search({
                        $and: [
                            ...keyWordQuery,
                            ...filterQuery,
                            ...priceFilterQuery,
                            {
                                $or: [
                                    {
                                        'category.parentCategoryId': {
                                            $eq: categoryId,
                                        },
                                    },
                                    {
                                        'category.id': {
                                            $eq: categoryId,
                                        },
                                    },
                                ],
                            },
                        ],
                    });
                }
            } else {
                qb.search({
                    $and: [
                        ...keyWordQuery,
                        ...filterQuery,
                        ...priceFilterQuery,
                        ...brandFilterQuery,
                        {
                            'category.parentCategoryId': {
                                $notin: [
                                    fashionCategories?.[0]?.categoryId,
                                    accessoryCategories?.[0]?.categoryId,
                                    foodCategories?.[0]?.categoryId,
                                    athleisureCategories?.[0]?.categoryId,
                                    supplementCategories?.[0]?.categoryId,
                                ],
                            },
                        },
                        {
                            'category.id': {
                                $notin: [
                                    fashionCategories?.[0]?.categoryId,
                                    accessoryCategories?.[0]?.categoryId,
                                    foodCategories?.[0]?.categoryId,
                                    athleisureCategories?.[0]?.categoryId,
                                    supplementCategories?.[0]?.categoryId,
                                ],
                            },
                        },
                    ],
                });
            }

            switch (sort) {
                case 'newest':
                    qb.sortBy([
                        {
                            field: 'createdAt',
                            order: 'DESC',
                        },
                    ]);
                    break;
                case 'priceHigh':
                    qb.sortBy([
                        {
                            field: 'representMallProduct.productCharge',
                            order: 'DESC',
                        },
                    ]);
                    break;
                case 'priceLow':
                    qb.sortBy([
                        {
                            field: 'representMallProduct.productCharge',
                            order: 'ASC',
                        },
                    ]);
                    break;
                case 'name':
                    qb.sortBy([
                        {
                            field: 'groupTitle',
                            order: 'ASC',
                        },
                    ]);
                    break;
                default:
                    qb.sortBy([
                        {
                            field: 'createdAt',
                            order: 'DESC',
                        },
                    ]);
                    break;
            }
            qb.setOffset(offset).setLimit(limit);
            const query = qb.query(false);
            const { data: responseData } = await api.get<PickResponse>(`seller/picks?${query}`, {
                baseURL: apiUrls.matching,
            });
            const { data: list, total } = responseData;

            console.log(`상품 로딩 결과: 총 ${total}개 중 ${list.length}개 가져옴`);

            // 검색어가 있지만 결과가 없는 경우
            if (keyword && offset === 0 && list.length === 0) {
                setHasMore(false);
                setProductList([]);
                return;
            }

            // 처음 로딩이면 리스트 초기화, 그 외에는 기존 리스트에 추가
            if (offset === 0) {
                setProductList(list);
            } else {
                setProductList((prev) => {
                    const existingProductIds = new Set(prev.map((item) => item.id));
                    const newUniqueItems = list.filter((item) => !existingProductIds.has(item.id));
                    console.log(`중복 제거 후 ${newUniqueItems.length}개의 새 상품 추가`);
                    return [...prev, ...newUniqueItems];
                });
            }

            // 더 불러올 데이터가 있는지 확인
            const hasMoreData = total > offset + list.length;
            console.log(`더 불러올 데이터 여부: ${hasMoreData ? '있음' : '없음'}`);
            setHasMore(hasMoreData);
        } catch (error) {
            console.error('상품 로딩 중 오류 발생:', error);
            toast.error('상품 로딩 중 오류가 발생했습니다. 다시 시도해주세요.', {
                autoClose: 2000,
                position: 'top-center',
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchFilter = async ({ categoryId }: { categoryId?: string }) => {
        const qb = RequestQueryBuilder.create();
        if (categoryId) {
            qb.search({
                $or: [
                    {
                        'categories.id': {
                            $eq: categoryId,
                        },
                    },
                    {
                        'categories.parentCategoryId': {
                            $eq: categoryId,
                        },
                    },
                ],
            });
        } else {
            qb.search({
                $and: [
                    {
                        'categories.parentCategoryId': {
                            $notin: [
                                fashionCategories?.[0]?.categoryId,
                                accessoryCategories?.[0]?.categoryId,
                                foodCategories?.[0]?.categoryId,
                                athleisureCategories?.[0]?.categoryId,
                                supplementCategories?.[0]?.categoryId,
                            ],
                        },
                    },
                    {
                        'categories.id': {
                            $notin: [
                                fashionCategories?.[0]?.categoryId,
                                accessoryCategories?.[0]?.categoryId,
                                foodCategories?.[0]?.categoryId,
                                athleisureCategories?.[0]?.categoryId,
                                supplementCategories?.[0]?.categoryId,
                            ],
                        },
                    },
                ],
            });
        }
        const queryString = qb.query(false);
        const { data } = await api.get<FilterResponse>(`filters?${queryString}`, {
            baseURL: apiUrls.matching,
        });
        data.unshift({
            id: 'price',
            memo: '',
            name: '가격대',
            categories: [],
            filterOptions: [
                {
                    id: 'price_1',
                    value: '1만원',
                    description: '',
                },
                {
                    id: 'price_2',
                    value: '2만원',
                    description: '',
                },
                {
                    id: 'price_3',
                    value: '3만원',
                    description: '',
                },
                {
                    id: 'price_4',
                    value: '4만원',
                    description: '',
                },
                {
                    id: 'price_5',
                    value: '5만원 이상',
                    description: '',
                },
            ],
        });
        setFilterData(data || []);
    };

    const fetchBrandFilter = async () => {
        const { data } = await api.get('/seller/picks/brand-filter', { baseURL: apiUrls.matching });
        const brandFilterData = data?.items?.map((item: any) => ({
            brandName: item.value,
            brandImageUrl: item.filterOptionImages?.[0]?.imageUrl,
        }));
        setBrandFilterData(brandFilterData);
    };

    // 검색어 변경 핸들러
    const handleSearchChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const handleTabChange = async (event: React.SyntheticEvent, newValue: number) => {
        setSelectedTabIndex(newValue);
        setProductList(() => []);
        setAppliedFilters({});
        setSelectedFilters({});
        setLoading(() => false);
        setHasMore(() => true);
        await fetchProducts({
            offset: 0,
            limit,
            categoryId: filteredCategories?.[newValue]?.categoryId,
            keyword: searchTerm,
            sort: sortOption,
        });
    };

    const handle09RequestClick = async ({ productGroupId }: { productGroupId: string }) => {
        try {
            await request09ByProductId({
                productGroupId: productGroupId,
                sellerId: spaceUserData?.user_id,
            });
            toast.success('공구 요청 완료');
        } catch (error: any) {
            if (error['data']) {
                toast.error(error?.data?.placeHolder || error?.data?.message || '공구 요청에 실패했습니다.', {
                    toastId: `09RequestError_${productGroupId}`,
                    position: 'top-center',
                });
                return;
            }
            toast.error('공구 요청에 실패했습니다.');
        }
    };

    const refreshPartnersBlocks = async () => {
        const blockData = (await getEventBlockV2(spaceUserData?.user_id!)) as any[];
        const partnersBlocks = blockData?.filter?.((block) => block?.['block_type'] == 'partners');
        const getPartnersProductListPromises = partnersBlocks.map((block) =>
            getPartnersProductListWithBlockId({
                user_id: spaceUserData?.user_id!,
                block_id: block?.block_id,
            })
                .then((partnersProducts) => ({
                    blockId: block?.block_id,
                    productIds: partnersProducts?.map(({ product_id }) => product_id) || [],
                }))
                .catch((e) => {
                    console.error(e);
                    return {
                        blockId: block?.block_id,
                        productIds: [],
                    };
                }),
        );

        const results = await Promise.all(getPartnersProductListPromises);

        const partnersBlockProductIds = results.reduce(
            (acc, { blockId, productIds }) => {
                acc[blockId] = productIds;
                return acc;
            },
            {} as Record<string, string[]>,
        );
        const addedProducts = new Set(Object.values(partnersBlockProductIds).flat());
        setPartnersBlocks(partnersBlocks);
        setAddedProducts(addedProducts);
        setPartnersBlockProductIds(partnersBlockProductIds);
    };

    const handleCategoryTypeChange = (
        type: 'living' | 'fashion' | 'accessory' | 'athleisure' | 'food' | 'supplement',
    ) => {
        setCategoryType(type);
        setSelectedTabIndex(0);
        setAppliedFilters({});
        setSelectedFilters({});
        setBrandFilterData([]);
        setLoading(false);
        setHasMore(true);
        switch (type) {
            case 'living':
                setFilteredCategories(livingCategories);
                break;
            case 'fashion':
                setFilteredCategories(fashionCategories);
                break;
            case 'accessory':
                setFilteredCategories(accessoryCategories);
                break;
            case 'athleisure':
                setFilteredCategories(athleisureCategories);
                break;
            case 'food':
                setFilteredCategories(foodCategories);
                break;
            case 'supplement':
                setFilteredCategories(supplementCategories);
                break;
        }
    };

    const getFilterOptionImage = async (filterOptionId: string) => {
        try {
            // 세션 스토리지 키 생성
            const storageKey = `filter_option_image_${filterOptionId}`;

            // 세션 스토리지에서 데이터 확인
            const cachedData = sessionStorage.getItem(storageKey);

            if (cachedData) {
                const parsedData = JSON.parse(cachedData);

                // 캐시 만료 시간 확인 (5분 = 300000ms)
                const fiveMinutes = 5 * 60 * 1000;
                if (parsedData.timestamp && Date.now() - parsedData.timestamp < fiveMinutes) {
                    console.log('캐시된 필터 옵션 이미지 데이터 사용:', filterOptionId);
                    return parsedData.data as FilterOptionImage[];
                }

                // 만료된 캐시 데이터 삭제
                sessionStorage.removeItem(storageKey);
            }

            // API 호출하여 데이터 가져오기
            const qb = RequestQueryBuilder.create();
            qb.setFilter({
                field: 'optionId',
                operator: '$eq',
                value: filterOptionId,
            });
            const queryStr = qb.query(false);
            const { data } = await api.get<FilterOptionImage[]>(`filters/options/images?${queryStr}`, {
                baseURL: apiUrls.matching,
            });

            // 세션 스토리지에 데이터 저장 (타임스탬프 포함)
            const cacheData = {
                data,
                timestamp: Date.now(),
            };
            sessionStorage.setItem(storageKey, JSON.stringify(cacheData));

            return data;
        } catch (error) {
            console.error('필터 옵션 이미지 조회 실패:', error);
            throw error;
        }
    };

    return (
        <>
            {/* 패션/리빙 카테고리 전환 컴포넌트 */}
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Box
                    sx={{
                        mb: 4,
                        display: 'flex',
                        gap: 1,
                        '& svg': { width: '16px', height: '16px', color: 'inherit' },
                    }}
                >
                    <Button
                        variant={categoryType === 'athleisure' ? 'contained' : 'outlined'}
                        startIcon={<FitnessCenterIcon />}
                        onClick={() => handleCategoryTypeChange('athleisure')}
                    >
                        애슬레저
                    </Button>
                    <Button
                        variant={categoryType === 'fashion' ? 'contained' : 'outlined'}
                        startIcon={<ReactSVG src={fashionIcon} />}
                        onClick={() => handleCategoryTypeChange('fashion')}
                    >
                        패션
                    </Button>
                    <Button
                        variant={categoryType === 'accessory' ? 'contained' : 'outlined'}
                        startIcon={<ReactSVG src={accessoryIcon} />}
                        onClick={() => handleCategoryTypeChange('accessory')}
                    >
                        악세사리
                    </Button>
                    <Button
                        variant={categoryType === 'supplement' ? 'contained' : 'outlined'}
                        startIcon={<MedicationIcon />}
                        onClick={() => handleCategoryTypeChange('supplement')}
                    >
                        건강기능식품
                    </Button>
                    <Button
                        variant={categoryType === 'food' ? 'contained' : 'outlined'}
                        startIcon={<RestaurantIcon />}
                        onClick={() => handleCategoryTypeChange('food')}
                    >
                        식품
                    </Button>
                    <Button
                        variant={categoryType === 'living' ? 'contained' : 'outlined'}
                        startIcon={<ReactSVG src={livingIcon} />}
                        onClick={() => handleCategoryTypeChange('living')}
                    >
                        리빙
                    </Button>
                </Box>

                {/* 검색 영역 */}
                <Box
                    sx={{
                        mb: 4,
                        position: 'sticky',
                        top: 16,
                        zIndex: 10,
                        transition: 'all 0.2s ease',
                    }}
                >
                    <Paper
                        elevation={2}
                        sx={{
                            borderRadius: '30px',
                            overflow: 'hidden',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                            transition: 'box-shadow 0.2s ease',
                            p: 0,
                            '&:hover': {
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            },
                            '& > div:first-of-type': {
                                p: '0',
                                pb: '18px',
                                pt: '8px',
                            },
                        }}
                    >
                        <TextField
                            fullWidth
                            placeholder="어떤 상품을 찾고 계신가요?"
                            variant="standard" // 테두리 없는 스타일
                            value={searchTerm}
                            onChange={handleSearchChange}
                            InputProps={{
                                disableUnderline: true, // 밑줄 제거
                                startAdornment: (
                                    <InputAdornment position="start" sx={{ ml: 1.5 }}>
                                        <SearchIcon sx={{ color: '#FF385C', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchTerm && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchTerm('')} sx={{ mr: 1 }}>
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                sx: {
                                    height: 20,
                                    padding: '0 8px',
                                    fontSize: '15px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    '& input': {
                                        pl: 1,
                                        fontWeight: 500,
                                        '&::placeholder': {
                                            opacity: 0.7,
                                            verticalAlign: 'middle',
                                        },
                                    },
                                },
                            }}
                        />
                    </Paper>
                </Box>

                {/* 카테고리 탭 */}
                <Box sx={{ mb: 4, borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                        value={selectedTabIndex}
                        onChange={handleTabChange}
                        variant={'scrollable'}
                        scrollButtons={'auto'}
                        allowScrollButtonsMobile
                        sx={{
                            transition: 'transform 0.1s ease',
                        }}
                    >
                        {filteredCategories?.map((category, index) => (
                            <Tab
                                key={'Tab' + index}
                                value={index}
                                sx={{}}
                                label={
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            transition: 'transform 0.1s ease',
                                            '&:hover': {
                                                transform: 'scale(1.05)',
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: '42px',
                                                height: '42px',
                                                overflow: 'hidden',
                                                borderRadius: '50%',
                                                background: 'white',
                                                boxShadow: 'var(--v-shadows-1)',
                                                padding: '6px',
                                            }}
                                        >
                                            {category.imageUrl ? (
                                                <img
                                                    src={category.imageUrl}
                                                    alt={category.categoryName}
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.style.display = 'none';
                                                        // 이미지 오류 시 부모 요소에 대체 UI 표시
                                                        const parent = target.parentElement;
                                                        console.log('parent :>> ', parent);
                                                        if (parent) {
                                                            parent.classList.add('image-error');
                                                            // 카테고리 이름의 첫 글자를 표시
                                                            const firstLetter = document.createElement('div');
                                                            firstLetter.className = 'category-fallback';
                                                            firstLetter.textContent = category.categoryName.charAt(0);
                                                            firstLetter.style.width = '100%';
                                                            firstLetter.style.height = '100%';
                                                            firstLetter.style.display = 'flex';
                                                            firstLetter.style.alignItems = 'center';
                                                            firstLetter.style.justifyContent = 'center';
                                                            firstLetter.style.fontSize = '18px';
                                                            firstLetter.style.fontWeight = 'bold';
                                                            firstLetter.style.color = '#666';
                                                            firstLetter.style.backgroundColor = '#f5f5f5';
                                                            firstLetter.style.borderRadius = '50%';
                                                            parent.appendChild(firstLetter);
                                                        }
                                                    }}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        objectPosition: 'center',
                                                    }}
                                                />
                                            ) : (
                                                // 이미지가 없을 때 대체 UI
                                                <div
                                                    className="category-fallback"
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '18px',
                                                        fontWeight: 'bold',
                                                        color: '#666',
                                                        backgroundColor: '#f5f5f5',
                                                        borderRadius: '50%',
                                                    }}
                                                >
                                                    {category.categoryName.charAt(0)}
                                                </div>
                                            )}
                                        </Box>
                                        <Typography variant="body2">{category.categoryName}</Typography>
                                    </Box>
                                }
                            />
                        ))}
                    </Tabs>
                </Box>
                {/* 필터 기능 */}
                <Box sx={{ mb: 2, mt: 2 }}>
                    {/* 필터 아코디언 */}
                    <Accordion
                        expanded={expandedFilter}
                        onChange={() => setExpandedFilter(!expandedFilter)}
                        sx={{ boxShadow: 1, mb: 2 }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="filter-content"
                            id="filter-header"
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Badge
                                    badgeContent={Object.keys(selectedFilters)?.length || 0}
                                    color="primary"
                                    sx={{ mr: 1 }}
                                >
                                    <FilterListIcon />
                                </Badge>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                                    필터 옵션
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={{ p: 1 }}>
                                {/* 필터 옵션 반복문으로 그리기 */}
                                {filterData.map((filter) => (
                                    <Fragment key={filter.id}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            {`${filter.name} ${
                                                selectedTabIndex == 0 &&
                                                filter?.categories?.filter(({ id }) =>
                                                    filteredCategories?.some(({ categoryId }) => categoryId == id),
                                                )?.length > 0
                                                    ? `(${filter?.categories
                                                          ?.filter(({ id }) =>
                                                              filteredCategories?.some(
                                                                  ({ categoryId }) => categoryId == id,
                                                              ),
                                                          )
                                                          ?.map(({ name }) => name)
                                                          .join(', ')})`
                                                    : ''
                                            }`}
                                        </Typography>
                                        <Grid container spacing={1} sx={{ ml: 0, mb: 2 }}>
                                            {filter?.filterOptions?.map((option) => {
                                                const isSelected = !!selectedFilters[option.id];
                                                return (
                                                    <Grid
                                                        item
                                                        xs={6}
                                                        sm={4}
                                                        md={3}
                                                        key={`filter-options-${filter.id}-${option.id}`}
                                                    >
                                                        <Box
                                                            sx={{
                                                                py: 0.2,
                                                                px: 0.5,
                                                                borderRadius: 1,
                                                                backgroundColor: isSelected
                                                                    ? 'action.selected'
                                                                    : 'transparent',
                                                                borderColor: isSelected ? 'divider' : 'transparent',
                                                                transition: 'all 0.2s ease',
                                                                '&:hover': {
                                                                    backgroundColor: isSelected
                                                                        ? 'action.selected'
                                                                        : 'action.hover',
                                                                },
                                                            }}
                                                        >
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        size="small"
                                                                        checked={!!isSelected}
                                                                        onChange={(e) => {
                                                                            if (e.target.checked) {
                                                                                setSelectedFilters({
                                                                                    ...selectedFilters,
                                                                                    [option.id]: option,
                                                                                });
                                                                            } else {
                                                                                setSelectedFilters((prev) => {
                                                                                    delete prev[option.id];
                                                                                    return {
                                                                                        ...prev,
                                                                                    };
                                                                                });
                                                                            }
                                                                        }}
                                                                    />
                                                                }
                                                                label={
                                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                fontWeight: isSelected ? 500 : 400,
                                                                                color: isSelected
                                                                                    ? 'primary.main'
                                                                                    : 'text.primary',
                                                                            }}
                                                                        >
                                                                            {option.value}
                                                                        </Typography>
                                                                        {option.description && (
                                                                            <>
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={async (e) => {
                                                                                        e.preventDefault();
                                                                                        e.stopPropagation();
                                                                                        const images =
                                                                                            await getFilterOptionImage(
                                                                                                option.id,
                                                                                            );
                                                                                        dialogOpen({
                                                                                            title: option.value,
                                                                                            maxWidth: 'sm',
                                                                                            innerElement: (
                                                                                                <>
                                                                                                    {images &&
                                                                                                        images.length >
                                                                                                            0 && (
                                                                                                            <Box
                                                                                                                sx={{
                                                                                                                    p: 3,
                                                                                                                    width: '100%',
                                                                                                                    display:
                                                                                                                        'flex',
                                                                                                                    justifyContent:
                                                                                                                        'center',
                                                                                                                }}
                                                                                                            >
                                                                                                                <div
                                                                                                                    style={{
                                                                                                                        position:
                                                                                                                            'relative',
                                                                                                                        width: 'fit-content',
                                                                                                                        maxWidth:
                                                                                                                            '100%',
                                                                                                                        margin: '0 auto',
                                                                                                                        borderRadius:
                                                                                                                            '12px',
                                                                                                                        overflow:
                                                                                                                            'hidden',
                                                                                                                        boxShadow:
                                                                                                                            '0 6px 16px rgba(0, 0, 0, 0.12)',
                                                                                                                        transition:
                                                                                                                            'transform 0.3s ease, box-shadow 0.3s ease',
                                                                                                                    }}
                                                                                                                >
                                                                                                                    {/* 기준 이미지 */}
                                                                                                                    <img
                                                                                                                        src={
                                                                                                                            images[0]
                                                                                                                                ?.baseImage
                                                                                                                                ?.imageUrl
                                                                                                                        }
                                                                                                                        alt="Base Image"
                                                                                                                        style={{
                                                                                                                            width: '100%',
                                                                                                                            transition:
                                                                                                                                'opacity 0.3s ease',
                                                                                                                        }}
                                                                                                                    />
                                                                                                                    {/* 오버레이 이미지 */}
                                                                                                                    <img
                                                                                                                        src={
                                                                                                                            images[0]
                                                                                                                                ?.imageUrl
                                                                                                                        }
                                                                                                                        alt="Overlay Image"
                                                                                                                        style={{
                                                                                                                            position:
                                                                                                                                'absolute',
                                                                                                                            top: 0,
                                                                                                                            left: 0,
                                                                                                                            width: '100%',
                                                                                                                            objectFit:
                                                                                                                                'cover',
                                                                                                                            opacity: 0.95,
                                                                                                                        }}
                                                                                                                    />
                                                                                                                </div>
                                                                                                            </Box>
                                                                                                        )}
                                                                                                    <Box
                                                                                                        sx={{
                                                                                                            p: 2,
                                                                                                            display:
                                                                                                                'flex',
                                                                                                            justifyContent:
                                                                                                                'center',
                                                                                                            mt: 1,
                                                                                                        }}
                                                                                                    >
                                                                                                        <Chip
                                                                                                            size="small"
                                                                                                            color="primary"
                                                                                                            sx={{
                                                                                                                padding:
                                                                                                                    '6px 12px',
                                                                                                                fontSize:
                                                                                                                    '14px',
                                                                                                                fontWeight: 500,
                                                                                                                borderRadius:
                                                                                                                    '16px',
                                                                                                                boxShadow:
                                                                                                                    '0 2px 4px rgba(0, 0, 0, 0.08)',
                                                                                                                height: 'auto',
                                                                                                                '& .MuiChip-label':
                                                                                                                    {
                                                                                                                        display:
                                                                                                                            'block',
                                                                                                                        whiteSpace:
                                                                                                                            'normal',
                                                                                                                    },
                                                                                                            }}
                                                                                                            label={
                                                                                                                option.description
                                                                                                            }
                                                                                                        />
                                                                                                    </Box>
                                                                                                </>
                                                                                            ),
                                                                                        });
                                                                                    }}
                                                                                    sx={{ ml: 0.5, p: 0.5 }}
                                                                                >
                                                                                    <HelpOutlineIcon
                                                                                        fontSize="small"
                                                                                        color="action"
                                                                                    />
                                                                                </IconButton>
                                                                            </>
                                                                        )}
                                                                    </Box>
                                                                }
                                                                sx={{ m: 0 }}
                                                            />
                                                        </Box>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                        {<Divider sx={{ my: 2 }} />}
                                        {/* {filterType !== 'material' && <Divider sx={{ my: 2 }} />} */}
                                    </Fragment>
                                ))}
                                {/* 버튼 영역 */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            setSelectedFilters({});
                                        }}
                                    >
                                        초기화
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={() => {
                                            setAppliedFilters(selectedFilters);
                                            setExpandedFilter(false);
                                        }}
                                        startIcon={<FilterAltIcon />}
                                    >
                                        필터 적용
                                    </Button>
                                </Box>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Box>

                {/* 정렬 옵션 */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        mb: 2,
                        mt: 2,
                    }}
                >
                    {/* 브랜드 로고 (좌측) */}
                    {brandFilterData.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-end', mr: 2, gap: 2, mb: 1 }}>
                            {brandFilterData.map((brand, idx) => {
                                const isSelected = selectedBrandName === brand.brandName;
                                return (
                                    <Box
                                        key={brand.brandName + idx}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            cursor: 'pointer',
                                            border: isSelected ? '2px solid' : '2px solid transparent',
                                            borderColor: isSelected ? 'primary.main' : 'transparent',
                                            borderRadius: 2,
                                            transition: 'border 0.15s',
                                            p: 0.5,
                                        }}
                                        onClick={() => {
                                            setSelectedBrandName(brand.brandName);
                                            fetchProducts({
                                                offset: 0,
                                                limit,
                                                categoryId: filteredCategories?.[selectedTabIndex]?.categoryId,
                                                keyword: debouncedSearchTerm,
                                                sort: sortOption,
                                                filterOptionIds: [...Object.keys(appliedFilters)],
                                                brandName: brand.brandName,
                                            });
                                        }}
                                    >
                                        {brand.brandImageUrl ? (
                                            <img
                                                src={brand.brandImageUrl}
                                                alt={brand.brandName}
                                                style={{
                                                    height: 48,
                                                    width: 48,
                                                    objectFit: 'contain',
                                                    borderRadius: 8,
                                                    background: '#fff',
                                                }}
                                            />
                                        ) : (
                                            <Box
                                                sx={{
                                                    width: 48,
                                                    height: 48,
                                                    borderRadius: 8,
                                                    background: '#eee',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: 20,
                                                    color: '#888',
                                                }}
                                            >
                                                {brand.brandName.charAt(0)}
                                            </Box>
                                        )}
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                whiteSpace: 'nowrap',
                                                fontWeight: 500,
                                                textAlign: 'center',
                                                mt: -1.5,
                                            }}
                                        >
                                            {brand.brandName}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                    {/* 선택된 필터 표시 영역 */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                        {/* 예시 필터 칩 */}
                        {Object.entries(appliedFilters).map(([filterType, option]) => {
                            return (
                                <Chip
                                    key={`${filterType}-${option.id}-selected-chip`}
                                    label={option?.value}
                                    size="small"
                                    onDelete={() => {
                                        setSelectedFilters((prev) => {
                                            delete prev[option.id];
                                            return {
                                                ...prev,
                                            };
                                        });
                                        setAppliedFilters((prev) => {
                                            delete prev[option.id];
                                            return {
                                                ...prev,
                                            };
                                        });
                                    }}
                                    sx={{ m: 0.2 }}
                                />
                            );
                        })}
                        {/* {Object.entries(selectedFilters).flatMap(([filterType, selectedValues]) =>
                            selectedValues.map((value) => {
                                const option = filterOptions[filterType as keyof typeof filterOptions].options.find(
                                    (opt) => opt.value === value,
                                );
                                return (
                                    <Chip
                                        key={`${filterType}-${value}-selected-chip`}
                                        label={option?.value}
                                        size="small"
                                        onDelete={() => {
                                            const newSelected = [
                                                ...selectedFilters[filterType as keyof typeof selectedFilters],
                                            ];
                                            const index = newSelected.indexOf(value);
                                            if (index !== -1) newSelected.splice(index, 1);

                                            setSelectedFilters({
                                                ...selectedFilters,
                                                [filterType]: newSelected,
                                            });

                                            setSelectedFilterCount((prev) => prev - 1);
                                        }}
                                        sx={{ m: 0.2 }}
                                    />
                                );
                            }),
                        )} */}
                    </Box>
                    <Box flex={1} />
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel id="sort-select-label">정렬</InputLabel>
                        <Select
                            labelId="sort-select-label"
                            value={sortOption}
                            label="정렬"
                            onChange={(e) => setSortOption(e.target.value as typeof sortOption)}
                            sx={{
                                '& .MuiSelect-select': {
                                    display: 'flex',
                                    alignItems: 'center',
                                },
                            }}
                        >
                            <MenuItem value="newest">
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <SortIcon sx={{ mr: 1, fontSize: '1rem' }} />
                                    최신순
                                </Box>
                            </MenuItem>
                            <MenuItem value="priceHigh">
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <ArrowUpwardIcon sx={{ mr: 1, fontSize: '1rem' }} />
                                    가격 높은순
                                </Box>
                            </MenuItem>
                            <MenuItem value="priceLow">
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <ArrowDownwardIcon sx={{ mr: 1, fontSize: '1rem' }} />
                                    가격 낮은순
                                </Box>
                            </MenuItem>
                            <MenuItem value="name">
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <SortByAlphaIcon sx={{ mr: 1, fontSize: '1rem' }} />
                                    이름순
                                </Box>
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {/* 상품 그리드 */}
                <Grid container spacing={2} sx={{}}>
                    {productList?.map((product, index) => {
                        // 마지막 요소에만 ref를 설정하여 무한 스크롤 트리거 포인트로 사용
                        const isLastElement = index === productList.length - 1;
                        return (
                            <Grid
                                item
                                xs={6}
                                sm={6}
                                md={3}
                                xl={2}
                                key={'filteredProducts_' + product.id + '_' + index}
                                ref={isLastElement ? lastProductElementRef : null}
                                sx={{
                                    width: '100%',
                                }}
                            >
                                <Card
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        overflow: 'hidden',
                                        borderRadius: '0',
                                    }}
                                >
                                    <Box
                                        sx={{ position: 'relative' }}
                                        onClick={() => {
                                            setDialogProductData({
                                                mallId: product.mallId,
                                                productId: product.id,
                                                matchingProductGroupId:
                                                    product.matchingContractProductMapper?.contract?.productGroupId,
                                                saleCommission:
                                                    product?.partnersProductSettlement?.settlementRate ||
                                                    product?.matchingContractProductMapper?.contract?.productGroup
                                                        ?.category.partnersCategorySettlement.settlementRate,
                                            });
                                            setOpenDetailDialog(!openDetailDialog);
                                        }}
                                    >
                                        <Box sx={{ overflow: 'hidden', position: 'relative' }}>
                                            <CardMedia
                                                component="img"
                                                image={product.representImageUrl}
                                                alt={product.groupTitle}
                                                sx={{
                                                    objectFit: 'cover',
                                                    aspectRatio: categoryType === 'fashion' ? '4/5' : '1/1',
                                                    objectPosition: 'center',
                                                    transition: 'transform 0.1s ease-in-out',
                                                    '&:hover': {
                                                        transform: 'scale(1.05)',
                                                    },
                                                    cursor: `url('${searchIcon}'), auto`,
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.5,
                                            flexWrap: 'wrap',
                                            padding: '12px 16px',
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle"
                                            component="div"
                                            sx={{
                                                fontWeight: 'medium',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                lineHeight: 1.3,
                                                height: '2.6em',
                                                mb: 1,
                                            }}
                                        >
                                            {product.groupTitle}
                                        </Typography>
                                        <Box
                                            sx={{
                                                width: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                flex: '1',
                                            }}
                                        >
                                            <Typography
                                                variant="body1"
                                                color="text.secondary"
                                                sx={{ textDecoration: 'line-through' }}
                                            >
                                                {Number(product?.representMallProduct?.productPrice).toLocaleString() +
                                                    '원'}
                                            </Typography>
                                            <Typography variant="body2" color="error" fontWeight="300">
                                                {product?.representMallProduct?.discountMethod?.toLowerCase() ===
                                                'percent'
                                                    ? product?.representMallProduct?.discountValue + '%'
                                                    : product?.representMallProduct?.discountValue.toLocaleString() +
                                                      '원'}{' '}
                                                할인
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                // justifyContent: 'space-between',
                                                gap: 2,
                                                alignItems: 'flex-end',
                                                mt: 0.5,
                                            }}
                                        >
                                            <Typography variant="h6" fontWeight="bold" color="primary">
                                                {Number(product?.representMallProduct?.productCharge).toLocaleString() +
                                                    '원'}
                                            </Typography>
                                            {!!(
                                                product?.partnersProductSettlement?.settlementRate ||
                                                product?.matchingContractProductMapper?.contract?.productGroup?.category
                                                    ?.partnersCategorySettlement?.settlementRate
                                            ) && (
                                                <Tooltip title="프로필링크 판매 시 받는 수수료입니다" placement="top">
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                                            borderRadius: '4px',
                                                            px: 0.8,
                                                            py: 0.3,
                                                            cursor: 'help',
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            color="text.contrast"
                                                            sx={{
                                                                fontSize: '0.7rem',
                                                                fontWeight: 500,
                                                            }}
                                                        >
                                                            수수료{' '}
                                                            {~~Number(
                                                                product?.partnersProductSettlement?.settlementRate ||
                                                                    product?.matchingContractProductMapper?.contract
                                                                        ?.productGroup?.category
                                                                        .partnersCategorySettlement.settlementRate,
                                                            ) || '_'}
                                                            %
                                                        </Typography>
                                                    </Box>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* <Box
                                        sx={{
                                            flexGrow: 1,
                                            padding: '12px 16px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            backgroundColor: '#fafafa',
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle"
                                            component="div"
                                            sx={{
                                                fontWeight: 'medium',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                lineHeight: 1.3,
                                                height: '2.6em',
                                                mb: 1,
                                            }}
                                        >
                                            {product.product_name}
                                        </Typography>

                                        <Typography
                                            variant="subtitle"
                                            color="black"
                                            sx={{
                                                fontWeight: '400',
                                                fontSize: '0.95rem',
                                                mt: 'auto',
                                                mb: 1.5,
                                            }}
                                        >
                                            {Number(product?.price_info?.product_charge).toLocaleString() + '원'}
                                        </Typography>
                                    </Box> */}
                                    {/* card actions */}
                                    <Box
                                        sx={{
                                            mt: 'auto',
                                            display: 'flex',
                                            alignItems: 'center',
                                            backgroundColor: 'rgba(255, 135, 188, 0.1)',
                                            minHeight: '36px',
                                        }}
                                    >
                                        <Button
                                            startIcon={
                                                addedProducts?.has(product.id) ? (
                                                    <Box
                                                        component={'span'}
                                                        sx={{
                                                            '& svg': {
                                                                width: '16px',
                                                                height: '16px',
                                                            },
                                                        }}
                                                    >
                                                        <ReactSVG src={linkRemoveIcon} />
                                                    </Box>
                                                ) : (
                                                    <Box
                                                        component={'span'}
                                                        sx={{
                                                            '& svg': {
                                                                width: '16px',
                                                                height: '16px',
                                                            },
                                                        }}
                                                    >
                                                        <ReactSVG src={linkAddIcon} />
                                                    </Box>
                                                )
                                            }
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setProductIdForAddingProfileLink(product.id);
                                                setOpenAddingProfileLinkDialog(true);
                                            }}
                                            sx={{
                                                flex: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                height: '100%',
                                                border: 'none',
                                                boxShadow: 'none',
                                                padding: '15px 11px',
                                                '&:hover': {
                                                    border: 'none',
                                                    boxShadow: 'none',
                                                },
                                                '& > span:nth-of-type(1)': {
                                                    margin: '0',
                                                },
                                            }}
                                            color={addedProducts?.has(product.id) ? 'contrast' : 'primary'}
                                        >
                                            <Typography
                                                variant="caption"
                                                fontWeight={500}
                                                sx={{ mt: '2px' }}
                                                textOverflow={'ellipsis'}
                                                whiteSpace={'nowrap'}
                                                overflow={'hidden'}
                                            >
                                                {`${addedProducts?.has(product.id) ? '프로필링크 제거' : '프로필링크 추가'}`}
                                            </Typography>
                                        </Button>
                                        {product?.matchingContractProductMapper?.contract?.isGroupPurchaseAllowed ? (
                                            <Button
                                                startIcon={
                                                    request09Loading === product.id ? (
                                                        <CircularProgress size={18} sx={{ color: '#bdbdbd' }} />
                                                    ) : (
                                                        <Box
                                                            component={'span'}
                                                            sx={{
                                                                flex: '1',
                                                                '& svg': {
                                                                    width: '16px',
                                                                    height: '16px',
                                                                },
                                                            }}
                                                        >
                                                            <ReactSVG src={gpRequestIcon} />
                                                        </Box>
                                                    )
                                                }
                                                variant="outlined"
                                                color="contrast"
                                                sx={{
                                                    flex: 1,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    height: '100%',
                                                    border: 'none !important',
                                                    boxShadow: 'none',
                                                    padding: '15px 11px',
                                                    '&:hover': {
                                                        border: 'none',
                                                        boxShadow: 'none',
                                                    },
                                                    '& > span:nth-of-type(1)': {
                                                        margin: '0',
                                                    },
                                                }}
                                                onClick={async (e) => {
                                                    e.stopPropagation();
                                                    setRequest09Loading(product.id);
                                                    try {
                                                        dialogOpen({
                                                            innerElement: (
                                                                <RequestSourcingDialog
                                                                    productName={product?.groupTitle || ''}
                                                                    onSourcingRequest={async () => {
                                                                        await handle09RequestClick({
                                                                            productGroupId:
                                                                                product?.matchingContractProductMapper
                                                                                    ?.contract?.productGroupId,
                                                                        });
                                                                    }}
                                                                />
                                                            ),
                                                            maxWidth: 'sm',
                                                            id: 'sourcing-request-dialog',
                                                            sx: {
                                                                '& .MuiDialog-paper': {
                                                                    background: 'transparent',
                                                                },
                                                            },
                                                        });
                                                    } catch (error) {
                                                    } finally {
                                                        setRequest09Loading('');
                                                    }
                                                }}
                                            >
                                                <Typography variant="caption" fontWeight={500} sx={{ mt: '2px' }}>
                                                    {'공구 요청'}
                                                </Typography>
                                            </Button>
                                        ) : (
                                            <></>
                                        )}
                                    </Box>
                                </Card>
                            </Grid>
                        );
                    })}

                    {productList?.length === 0 && !loading && (
                        <Grid item xs={12}>
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                <ErrorOutlineIcon sx={{ fontSize: '48px', color: '#bdbdbd' }} />
                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                                    검색 결과가 없습니다.
                                    <br />
                                    다른 검색어나 카테고리를 선택해보세요.
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                    {loading && (
                        <Grid item xs={12}>
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                <CircularProgress />
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </Container>
            <ProductDetailDialog
                open={openDetailDialog}
                setOpen={setOpenDetailDialog}
                productData={dialogProductData}
                isAddedProfileLink={addedProducts?.has(dialogProductData.productId)}
                profileLinkActionButtonOnClick={(e) => {
                    e.stopPropagation();
                    setProductIdForAddingProfileLink(dialogProductData.productId);
                    setOpenAddingProfileLinkDialog(true);
                }}
                handle09RequestClick={async () => {
                    await handle09RequestClick({ productGroupId: dialogProductData.matchingProductGroupId || '' });
                }}
            />
            <Dialog
                open={openAddingProfileLinkDialog && !!productIdForAddingProfileLink}
                onClose={() => {
                    setOpenAddingProfileLinkDialog(false);
                    setTimeout(() => {
                        setProductIdForAddingProfileLink('');
                    }, 100);
                }}
            >
                <DialogTitle
                    sx={{
                        p: 3,
                        pb: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h5" fontWeight="bold">
                            {'블록별 스타일 모으기'}
                            &nbsp;
                            <Tooltip
                                title="어울리는 상품을 모아 나만의 마이크로 몰을 만들어보세요"
                                sx={{ cursor: 'help' }}
                            >
                                <HelpOutlineIcon fontSize="small" />
                            </Tooltip>
                        </Typography>
                        <IconButton
                            size="small"
                            disabled={refreshLoading}
                            onClick={async (e: React.MouseEvent) => {
                                e.stopPropagation();
                                if (refreshLoading) return; // 이미 로딩 중이면 중복 실행 방지

                                try {
                                    setRefreshLoading(true); // 로딩 상태 시작
                                    await refreshPartnersBlocks(); // 새로고침 함수 호출
                                    // toast.success('블록 목록을 새로고침했습니다.');
                                } catch (error) {
                                    // toast.error('블록 목록 새로고침에 실패했습니다.');
                                } finally {
                                    setRefreshLoading(false); // 로딩 상태 종료
                                }
                            }}
                            sx={{
                                bgcolor: 'rgba(25, 118, 210, 0.08)',
                                '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.12)' },
                                position: 'relative',
                            }}
                            aria-label="새로고침"
                        >
                            {refreshLoading ? (
                                <CircularProgress size={18} sx={{ color: '#bdbdbd' }} />
                            ) : (
                                <RefreshIcon fontSize="small" />
                            )}
                        </IconButton>
                    </Box>
                    <IconButton
                        aria-label="close"
                        onClick={() => {
                            setOpenAddingProfileLinkDialog(false);
                            setTimeout(() => {
                                setProductIdForAddingProfileLink('');
                            }, 100);
                        }}
                        sx={{ color: (theme) => theme.palette.grey[500] }}
                    >
                        <ClearIcon />
                    </IconButton>
                </DialogTitle>
                <Divider />
                <Box sx={{ width: '100%', overflow: 'auto' }}>
                    <Table stickyHeader aria-label="블록 및 상품 테이블">
                        <TableHead>
                            <TableRow>
                                <TableCell width="40%" sx={{ fontWeight: 'bold' }}>
                                    블록명
                                </TableCell>
                                <TableCell width="40%" sx={{ fontWeight: 'bold' }}>
                                    상품명
                                </TableCell>
                                <TableCell width="20%" align="center" sx={{ fontWeight: 'bold' }}>
                                    작업
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {partnersBlocks
                                ?.filter((block) => {
                                    if (addedProducts?.has(productIdForAddingProfileLink)) {
                                        return partnersBlockProductIds[block?.block_id]?.some((id) => {
                                            return id === productIdForAddingProfileLink;
                                        });
                                    } else {
                                        return partnersBlockProductIds[block?.block_id]?.every((id) => {
                                            return id !== productIdForAddingProfileLink;
                                        });
                                    }
                                })
                                ?.map((block, i) => {
                                    const isProductInBlock = addedProducts.has(productIdForAddingProfileLink);
                                    const productName =
                                        productList?.find((item) => item?.id === productIdForAddingProfileLink)
                                            ?.groupTitle || '';

                                    return (
                                        <TableRow key={'TableRow_' + block?.block_id + '_' + i}>
                                            <TableCell>{block?.block_content?.title || ''}</TableCell>
                                            <TableCell>{productName}</TableCell>
                                            <TableCell align="center">
                                                <Button
                                                    variant={isProductInBlock ? 'contained' : 'outlined'}
                                                    color={isProductInBlock ? 'error' : 'primary'}
                                                    size="small"
                                                    startIcon={isProductInBlock ? <RemoveIcon /> : <AddIcon />}
                                                    onClick={async () => {
                                                        const backupPartnersBlockProductIds = {
                                                            ...partnersBlockProductIds,
                                                        };
                                                        const productIds = partnersBlockProductIds[block?.block_id];

                                                        const itemIndex = productIds?.findIndex(
                                                            (id) => id === productIdForAddingProfileLink,
                                                        );

                                                        const isChecked = itemIndex !== -1;
                                                        if (isChecked) {
                                                            productIds?.splice(itemIndex, 1);
                                                        } else {
                                                            productIds?.unshift(productIdForAddingProfileLink);
                                                        }

                                                        const backupAddedProducts = new Set(addedProducts);
                                                        const newAddedProducts = new Set(
                                                            Object.values(partnersBlockProductIds).flat(),
                                                        );

                                                        setAddedProducts(newAddedProducts);
                                                        setPartnersBlockProductIds({ ...partnersBlockProductIds });
                                                        try {
                                                            if (isProductInBlock) {
                                                                if (
                                                                    block?.block_content?.partners_content?.length === 0
                                                                ) {
                                                                    return;
                                                                } else if (
                                                                    block?.block_content?.partners_content?.length === 1
                                                                ) {
                                                                    await deletePartnersBlockProduct({
                                                                        user_id: spaceUserData?.user_id,
                                                                        block_id: block?.block_id,
                                                                        partners_block_id:
                                                                            block?.block_content?.partners_content[0]
                                                                                ?.partners_id,
                                                                    });
                                                                } else {
                                                                    await putPartnersBlockProduct({
                                                                        user_id: spaceUserData?.user_id,
                                                                        block_id: block?.block_id,
                                                                        data: {
                                                                            product_ids: productIds,
                                                                        },
                                                                    });
                                                                }
                                                                toast.error(
                                                                    `${productName} 상품을 ${block?.block_content?.title || ''} 블록에서 제거했습니다.`,
                                                                );
                                                            } else {
                                                                await putPartnersBlockProduct({
                                                                    user_id: spaceUserData?.user_id,
                                                                    block_id: block?.block_id,
                                                                    data: {
                                                                        product_ids: productIds,
                                                                    },
                                                                });

                                                                toast.success(
                                                                    `${productName} 상품을 ${block?.block_content?.title || ''} 블록에 추가했습니다.`,
                                                                );
                                                            }
                                                        } catch (error) {}
                                                    }}
                                                >
                                                    {isProductInBlock ? '스타일 제거' : '스타일 추가'}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            {!addedProducts?.has(productIdForAddingProfileLink) && (
                                <TableRow>
                                    <TableCell colSpan={3}>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            startIcon={<AddIcon />}
                                            onClick={() => {
                                                (async () => {
                                                    try {
                                                        const newPartnersBlock = (
                                                            await postPartnersBlock({
                                                                user_id: spaceUserData?.user_id,
                                                                data: {
                                                                    product_ids: [productIdForAddingProfileLink],
                                                                    is_block_bundle: true,
                                                                },
                                                            })
                                                        )?.[0];
                                                        if (!newPartnersBlock) return;

                                                        await refreshPartnersBlocks();
                                                    } catch (error) {}
                                                })();
                                            }}
                                        >
                                            새로운 블록에 추가하기
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Box>
            </Dialog>
        </>
    );
};

export default PickPage;

function ProductDetailDialog({
    open,
    setOpen,
    productData,
    isAddedProfileLink,
    profileLinkActionButtonOnClick,
    handle09RequestClick,
}: {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    productData: { mallId: string; productId: string; saleCommission: string };
    isAddedProfileLink: boolean;
    profileLinkActionButtonOnClick: (e: React.MouseEvent) => void;
    handle09RequestClick: () => Promise<void>;
}) {
    const { spaceUserData } = useRouteLoaderData('/studio/:userId') as StudioLoaderData;
    const dialogContentRef = useRef<HTMLDivElement>(null);
    const [productDetail, setProductDetail] = useState<ProductDetail | null>(null);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [request09Loading, setRequest09Loading] = useState('');

    /* 상품 상세정보 조회 */
    useEffect(() => {
        if (!open) {
            if (productDetail) {
                setTimeout(() => {
                    setProductDetail(null);
                }, 100);
            }
            return;
        }

        (async () => {
            try {
                const cacheKey = `getProductDetail_${productData.mallId}_${productData.productId}`;
                const cachedProductDetailString = sessionStorage?.getItem(cacheKey);
                if (cachedProductDetailString) {
                    try {
                        const _productDetail = JSON.parse(cachedProductDetailString);
                        setProductDetail(_productDetail);
                        return;
                    } catch (e) {
                        throw e;
                    }
                }

                const _productDetail = await getProductDetail({
                    mallId: productData.mallId,
                    productId: productData.productId,
                });
                sessionStorage?.setItem(cacheKey, JSON.stringify(_productDetail));
                setProductDetail(_productDetail);
            } catch (error) {
                setOpen(false);
                toast.error('상품 상세정보 조회 중 오류가 발생했습니다.', {
                    autoClose: 2000,
                    toastId: productData?.productId,
                    position: 'top-center',
                });
            }
        })();
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                setShowScrollTop(true);
            } else {
                setShowScrollTop(false);
            }
        });

        return () => {
            setTimeout(() => {
                setProductDetail(null);
                observer.disconnect();
            }, 100);
        };
    }, [open]);

    // 이미지 캐러셀 관련 상태 및 핸들러
    const [activeImageIndex, setActiveImageIndex] = useState(0);

    // 다음 이미지로 이동
    const handleNextImage = () => {
        if (productDetail?.productImage && productDetail.productImage.length > 0) {
            setActiveImageIndex((prevIndex) =>
                prevIndex === productDetail.productImage.length - 1 ? 0 : prevIndex + 1,
            );
        }
    };

    // 이전 이미지로 이동
    const handlePrevImage = () => {
        if (productDetail?.productImage && productDetail.productImage.length > 0) {
            setActiveImageIndex((prevIndex) =>
                prevIndex === 0 ? productDetail.productImage.length - 1 : prevIndex - 1,
            );
        }
    };

    // 다이얼로그가 열릴 때 이미지 인덱스 초기화
    useEffect(() => {
        if (open) {
            setActiveImageIndex(0);
        }
    }, [open]);

    const scrollToTop = () => {
        dialogContentRef.current?.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };

    return (
        <Dialog
            open={open && !!productDetail}
            onClose={() => {
                setOpen(false);
            }}
            aria-labelledby="product-dialog-title"
            maxWidth="md"
            fullWidth
            sx={{
                transition: 'all 0.1s ease',
            }}
        >
            <DialogTitle
                id="product-dialog-title"
                sx={{
                    p: 3,
                    pb: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Typography variant="h5" fontWeight="bold">
                    {productDetail?.representProductName || ''}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={() => {
                        setOpen(false);
                    }}
                    sx={{ color: (theme) => theme.palette.grey[500] }}
                >
                    <ClearIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent
                ref={dialogContentRef}
                dividers
                sx={{ p: 3, position: 'relative' }}
                onScroll={(e) => {
                    if ((e?.target as any)?.scrollTop > 500) {
                        setShowScrollTop(true);
                    } else {
                        setShowScrollTop(false);
                    }
                }}
            >
                <Grid container spacing={4} position="relative">
                    <Grid item xs={12} md={6}>
                        <Box
                            sx={{
                                borderRadius: 1,
                                position: 'relative',
                            }}
                        >
                            {/* 메인 이미지 캐러셀 */}
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    height: '100%',
                                }}
                            >
                                {productDetail?.productImage && productDetail.productImage.length > 0 ? (
                                    <>
                                        <Box
                                            sx={{
                                                position: 'relative',
                                                width: '100%',
                                                height: '100%',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <img
                                                src={productDetail.productImage[activeImageIndex] || ''}
                                                alt={`${productDetail?.representProductName || ''} - 이미지 ${activeImageIndex + 1}`}
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).style.display = 'none';
                                                }}
                                                style={{
                                                    width: '100%',
                                                    objectFit: 'cover',
                                                    objectPosition: 'center',
                                                    aspectRatio: '4/5',
                                                    margin: '0 auto',
                                                    transition: 'opacity 0.3s ease-in-out',
                                                }}
                                            />
                                        </Box>

                                        {/* 이미지가 2개 이상일 때만 네비게이션 버튼 표시 */}
                                        {productDetail.productImage.length > 1 && (
                                            <>
                                                {/* 이전 이미지 버튼 */}
                                                <IconButton
                                                    onClick={handlePrevImage}
                                                    sx={{
                                                        position: 'absolute',
                                                        left: 8,
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                        },
                                                        zIndex: 2,
                                                    }}
                                                    size="small"
                                                >
                                                    <NavigateBeforeIcon />
                                                </IconButton>

                                                {/* 다음 이미지 버튼 */}
                                                <IconButton
                                                    onClick={handleNextImage}
                                                    sx={{
                                                        position: 'absolute',
                                                        right: 8,
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                        },
                                                        zIndex: 2,
                                                    }}
                                                    size="small"
                                                >
                                                    <NavigateNextIcon />
                                                </IconButton>
                                            </>
                                        )}
                                    </>
                                ) : productDetail?.representProductImage ? (
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            width: '100%',
                                            height: '100%',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <img
                                            src={productDetail?.representProductImage || ''}
                                            alt={`${productDetail?.representProductName || ''} - 이미지`}
                                            onError={(e) => {
                                                (e.target as HTMLImageElement).style.display = 'none';
                                            }}
                                            style={{
                                                width: '100%',
                                                objectFit: 'cover',
                                                objectPosition: 'center',
                                                aspectRatio: '4/5',
                                                margin: '0 auto',
                                                transition: 'opacity 0.3s ease-in-out',
                                            }}
                                        />
                                    </Box>
                                ) : (
                                    <img
                                        src={imageNotFound}
                                        alt="이미지를 찾을 수 없음"
                                        style={{
                                            width: '100%',
                                            objectFit: 'cover',
                                            objectPosition: 'center',
                                            aspectRatio: '4/5',
                                            margin: '0 auto',
                                        }}
                                    />
                                )}
                            </Box>

                            {/* 썸네일 이미지 목록 */}
                            {productDetail?.productImage && productDetail.productImage.length > 1 && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-start', // center에서 flex-start로 변경
                                        gap: 1,
                                        mt: 1,
                                        overflowX: 'auto',
                                        pb: 1,
                                        pl: 1, // 왼쪽 패딩 추가
                                        pr: 1, // 오른쪽 패딩 추가
                                        width: 'calc(100% - 8px)', // 패딩을 고려한 너비 조정
                                        '&::-webkit-scrollbar': {
                                            height: '4px',
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            backgroundColor: 'rgba(0,0,0,0.2)',
                                            borderRadius: '4px',
                                        },
                                        // 스크롤 이벤트 전파 방지를 위한 CSS 속성
                                        overscrollBehavior: 'contain',
                                        touchAction: 'pan-x',
                                    }}
                                    onWheel={(e) => {
                                        // React 합성 이벤트에서 네이티브 이벤트 추출
                                        const nativeEvent = e.nativeEvent;
                                        // 네이티브 이벤트에 대해 preventDefault와 stopPropagation 호출
                                        nativeEvent.stopPropagation();
                                        // 가로 스크롤 처리
                                        handleWheelHorizontalScroll(e as any);
                                        // 이벤트 전파 중지를 위해 false 반환
                                        return false;
                                    }}
                                >
                                    {productDetail.productImage.map((image, index) => (
                                        <Box
                                            key={index}
                                            onClick={() => setActiveImageIndex(index)}
                                            sx={{
                                                width: '60px',
                                                height: '60px',
                                                border: index === activeImageIndex ? '2px solid' : '1px solid',
                                                borderColor: index === activeImageIndex ? 'primary.main' : 'divider',
                                                borderRadius: 1,
                                                overflow: 'hidden',
                                                cursor: 'pointer',
                                                opacity: index === activeImageIndex ? 1 : 0.7,
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    opacity: 1,
                                                },
                                                flexShrink: 0,
                                            }}
                                        >
                                            <img
                                                src={image}
                                                alt={`썸네일 ${index + 1}`}
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).style.display = 'none';
                                                }}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                }}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <Typography variant="h4" fontWeight="bold" gutterBottom>
                                {productDetail?.representProductName || ''}
                            </Typography>

                            <Box sx={{ my: 2 }}>
                                {/* 가격 정보 테이블 */}
                                <Box sx={{ width: '100%', mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {/* 시중가 */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            py: 0.5,
                                            borderBottom: '1px solid #e0e0e0',
                                        }}
                                    >
                                        <Typography variant="body2" color="text.secondary">
                                            시중가
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {productDetail?.representProductPrice?.toLocaleString?.() || '0'}원
                                        </Typography>
                                    </Box>

                                    {/* 공구가 (할인된 가격) */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            py: 0.5,
                                            borderBottom: '1px solid #e0e0e0',
                                        }}
                                    >
                                        <Typography variant="body2" color="text.secondary">
                                            공구가
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {(productDetail?.totalCharge || '0')?.toLocaleString?.()}원
                                        </Typography>
                                    </Box>
                                    {/* 할인액 */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            py: 0.5,
                                            borderBottom: '1px solid #e0e0e0',
                                        }}
                                    >
                                        <Typography variant="body2" color="text.secondary">
                                            할인액
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {productDetail?.discountMethod?.toUpperCase() === 'PERCENT'
                                                ? productDetail?.discountValue + '%'
                                                : productDetail?.discountValue?.toLocaleString?.() + '원'}
                                        </Typography>
                                    </Box>
                                    {/* 수수료 */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            py: 0.5,
                                            borderBottom: '1px solid #e0e0e0',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                판매 수수료
                                            </Typography>
                                            <Tooltip title="프로필링크 판매 시 받는 수수료입니다" placement="top">
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        cursor: 'help',
                                                    }}
                                                >
                                                    <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                </Box>
                                            </Tooltip>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: 'primary.main',
                                                    fontWeight: 'medium',
                                                }}
                                            >
                                                {~~Number(productData.saleCommission) || '_'}%
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {/* 카테고리 정보 */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            py: 0.5,
                                            borderBottom: '1px solid #e0e0e0',
                                        }}
                                    >
                                        <Typography variant="body2" color="text.secondary">
                                            카테고리
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {productDetail?.category || ''}
                                        </Typography>
                                    </Box>
                                    {/* 배송비 */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            py: 0.5,
                                        }}
                                    >
                                        <Typography variant="body2" color="text.secondary">
                                            배송비
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'space-between',
                                                py: 0.5,
                                            }}
                                        >
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {productDetail?.productShippingPolicy?.shippingCost?.toLocaleString?.() ||
                                                        '0'}
                                                    원
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    무료배송 조건:{' '}
                                                    {productDetail?.productShippingPolicy?.freeShippingCostBar?.toLocaleString?.() ||
                                                        '0'}
                                                    원 이상 구매 시
                                                </Typography>
                                            </Box>
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    도서산간{': '}
                                                    {productDetail?.productShippingPolicy?.islandMountainShippingCost?.toLocaleString?.() ||
                                                        '0'}
                                                    원
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    배송비 할인 조건:{' '}
                                                    {productDetail?.productShippingPolicy?.islandMountainDiscountShippingCostBar?.toLocaleString?.() ||
                                                        '0'}
                                                    원 이상 구매 시{' '}
                                                    {productDetail?.productShippingPolicy?.islandMountainDiscountedPrice?.toLocaleString?.() ||
                                                        '0'}
                                                    원
                                                </Typography>
                                            </Box>
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    제주도{': '}
                                                    {productDetail?.productShippingPolicy?.jejuShippingCost?.toLocaleString?.() ||
                                                        '0'}
                                                    원
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    배송비 할인 조건:{' '}
                                                    {productDetail?.productShippingPolicy?.jejuDiscountShippingCostBar?.toLocaleString?.() ||
                                                        '0'}
                                                    원 이상 구매 시{' '}
                                                    {productDetail?.productShippingPolicy?.jejuDiscountedPrice?.toLocaleString?.() ||
                                                        '0'}
                                                    원
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                            <Box flex={1} />
                            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                <ProfileLinkActionButton
                                    isAddedProfileLink={isAddedProfileLink}
                                    onClick={profileLinkActionButtonOnClick}
                                    sx={{ flex: 1 }}
                                />
                                <Button
                                    variant="outlined"
                                    startIcon={
                                        request09Loading == productDetail?.productGroupId ? (
                                            <CircularProgress size={18} sx={{ color: '#bdbdbd' }} />
                                        ) : (
                                            <Box
                                                component={'span'}
                                                sx={{
                                                    '& svg': {
                                                        width: '13px',
                                                        height: '13px',
                                                        color: 'white',
                                                    },
                                                }}
                                            >
                                                <ReactSVG src={gpRequestIcon} />
                                            </Box>
                                        )
                                    }
                                    onClick={async (e) => {
                                        e.stopPropagation();
                                        setRequest09Loading(productDetail?.productGroupId!);
                                        try {
                                            await handle09RequestClick();
                                        } catch (error) {
                                        } finally {
                                            setRequest09Loading('');
                                        }
                                    }}
                                    color="contrast"
                                    sx={{
                                        color: 'contrast',
                                        width: '100%',
                                        height: '36px',
                                        boxShadow: 'none',
                                        flex: 1,
                                        transition: 'all 0.2s ease',
                                        borderColor: 'rgba(0, 0, 0, 0.12)',
                                        '&:hover': {
                                            borderColor: 'rgba(0, 0, 0, 0.3)',
                                            backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                        },
                                        '& > span:nth-of-type(1)': {
                                            margin: '0',
                                            mr: '3px',
                                        },
                                    }}
                                >
                                    <Typography variant="body2">공구 요청</Typography>
                                </Button>
                            </Box>
                        </Box>
                    </Grid>

                    {/* 제품 상세보기 영역 */}
                    <Grid item xs={12} sx={{ mt: 3 }}>
                        <Divider sx={{ mb: 3 }} />
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                            제품 상세정보
                        </Typography>
                        <Box mt={2} sx={{ width: '100%' }}>
                            {productDetail?.description ? (
                                <div
                                    className={'viewer'}
                                    dangerouslySetInnerHTML={{ __html: productDetail.description }}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        overflowWrap: 'break-word',
                                        wordBreak: 'break-word',
                                    }}
                                />
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    상세 정보가 없습니다.
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 100,
                        right: 24,
                        zIndex: 1300,
                        width: 'min-content',
                        transition: 'all 0.2s ease',
                        opacity: showScrollTop ? 1 : 0,
                        visibility: showScrollTop ? 'visible' : 'hidden',
                        transform: showScrollTop ? 'translateY(0)' : 'translateY(20px)',
                    }}
                >
                    <Fab
                        color="primary"
                        size="small"
                        aria-label="scroll to top"
                        onClick={scrollToTop}
                        sx={{
                            boxShadow: 3,
                            '&:hover': {
                                backgroundColor: 'primary.dark',
                            },
                        }}
                    >
                        <KeyboardArrowUpIcon />
                    </Fab>
                </Box>
                <Box />
                <Box>
                    <Button onClick={() => setOpen(false)} variant="outlined" sx={{ mr: 2 }}>
                        닫기
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}

function ProfileLinkActionButton({
    isAddedProfileLink,
    onClick,
    sx,
}: {
    isAddedProfileLink: boolean;
    onClick: (e: React.MouseEvent) => void;
    sx?: SxProps;
}) {
    const { t } = useTranslation();
    return (
        <Button
            startIcon={
                isAddedProfileLink ? (
                    <Box
                        component={'span'}
                        sx={{
                            '& svg': {
                                width: '13px',
                                height: '13px',
                                color: 'black',
                            },
                        }}
                    >
                        <ReactSVG src={linkRemoveIcon} />
                    </Box>
                ) : (
                    <Box
                        component={'span'}
                        sx={{
                            '& svg': {
                                width: '13px',
                                height: '13px',
                                color: 'white',
                            },
                        }}
                    >
                        <ReactSVG src={linkAddIcon} />
                    </Box>
                )
            }
            color={isAddedProfileLink ? 'contrast' : 'primary'}
            variant={isAddedProfileLink ? 'outlined' : 'outlined'}
            sx={{
                color: 'contrast',
                width: '100%',
                height: '100%',
                transition: 'all 0.1s ease',
                padding: '6px',
                '& > span:nth-of-type(1)': {
                    margin: '0',
                    mr: '3px',
                },
                borderColor: 'primary',
                ...sx,
            }}
            onClick={onClick}
        >
            <Typography variant="body2" textOverflow={'ellipsis'} whiteSpace={'nowrap'} overflow={'hidden'}>
                {`${isAddedProfileLink ? t('profile.removeProfileLink') : t('profile.addProfileLink')}`}
            </Typography>
        </Button>
    );
}

interface ProductDetail {
    productGroupId: string;
    representProductId: string;
    representProductName: string;
    representProductPrice: number;
    representProductImage: string;
    productImage: string[];
    discountMethod: string;
    discountValue: number;
    totalCharge: number;
    inventory: number;
    description: string;
    isForSale: boolean;
    isPublic: boolean;
    tagList: any[];
    productShippingPolicy: ProductShippingPolicy;
    ownerType: string;
    matchingSaleDate: MatchingSaleDate;
    discountPercent: string;
    vendorInfo: VendorInfo;
    mallReturnAddressInfo: MallReturnAddressInfo;
    mallCustomerCenterNumber: string;
    isQuizProduct: boolean;
    businessInfo: BusinessInfo;
    productExtraInfo: ProductExtraInfo;
    category: string;
}

interface ProductExtraInfo {
    manufacturer: null;
    countryOfOrigin: null;
    manufactureDate: null;
    afterServiceManager: null;
    afterServicePhone: null;
}

interface BusinessInfo {
    companyName: string;
    ownerName: string;
    businessRegistrationNumber: string;
    mailOrderSalesRegistrationNumber: string;
    customerCenterNumber: string;
    companyAddress: string;
}

interface MallReturnAddressInfo {
    postCode: null;
    recipientAddress: null;
    recipientAddressDetail: null;
    customerCenterNumber: null;
    returnAddressName: null;
    returnShippingCost: number;
}

interface VendorInfo {
    id: string;
    profileImage: string;
    name: string;
    customerCenterNumber: string;
    returnAddressName: null;
    returnShippingCost: number;
    returnAddress: ReturnAddress;
}

interface ReturnAddress {
    postCode: string;
    recipientAddress: string;
    recipientAddressDetail: string;
}

interface MatchingSaleDate {
    state: string;
    remainTime: RemainTime;
    startDate: string;
    endDate: string;
    message: string;
}

interface RemainTime {
    days: number;
    hours: number;
}

interface ProductShippingPolicy {
    id: string;
    shippingCost: number;
    islandMountainShippingCost: number;
    freeShippingCostBar: number;
    islandMountainDiscountShippingCostBar: number;
    islandMountainDiscountedPrice: number;
    jejuShippingCost: number;
    jejuDiscountShippingCostBar: number;
    jejuDiscountedPrice: number;
    isBundle: boolean;
    type: string;
}
